// A path router on the History API, and nothing more.
//
// The app has a handful of screens, each at one fixed path, and no
// nested layouts or route parameters yet. A routing library would be
// the larger thing to read for that; this file is the whole mechanism.
// docs/decisions.md in poweryourflow-ops records the choice.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

import type { AssessmentId } from "../schema/types";

export const paths = {
  entry: "/",
  welcome: "/boas-vindas",
  /** Conversa, SPEC-27: the screen a sign-in opens. */
  conversa: "/conversa",
  assessments: "/avaliacoes",
  signUp: "/criar-conta",
  signIn: "/entrar",
  /** Where the reset email's link lands; the email template's action URL (HANDOFF.md, step F7). */
  newPassword: "/nova-palavra-passe",
  /** Dados gerais, SPEC-18: after sign-up, and the way to edit it. */
  profileOnboarding: "/dados-gerais",
  terms: "/termos",
  privacy: "/privacidade",
  /** Perfil, SPEC-17. */
  profile: "/perfil",
  /** Dados gerais opened by Perfil's "Editar": it ends back on Perfil. */
  profileEdit: "/perfil/dados-gerais",
  // The assessments after Dados gerais, one path each; ICIQ is built (SPEC-19), the rest are placeholders until their cards.
  iciq: "/avaliacoes/perdas-de-urina",
  cycleSymptoms: "/avaliacoes/ciclo-sintomas",
  cycleSport: "/avaliacoes/ciclo-desporto",
  leafq: "/avaliacoes/lesoes-digestao-ciclo",
  sleep: "/avaliacoes/sono",
  beda: "/avaliacoes/alimentacao",
  ciss: "/avaliacoes/olhos-e-visao",
  fonseca: "/avaliacoes/maxilar-e-mastigacao",
  /** SPEC-33: the PYF team's Base de utilizadoras, outside the athlete app; no screen links here. */
  admin: "/admin",
} as const;

export type Path = (typeof paths)[keyof typeof paths];

/** Where each assessment opens, from the hub or from Conversa: Dados gerais in every state (an edit once done, SPEC-16 answer 3c). */
export const assessmentPaths: Record<AssessmentId, Path> = {
  dados: paths.profileOnboarding,
  iciq: paths.iciq,
  "ciclo-sintomas": paths.cycleSymptoms,
  "ciclo-desporto": paths.cycleSport,
  leafq: paths.leafq,
  sono: paths.sleep,
  beda: paths.beda,
  ciss: paths.ciss,
  fonseca: paths.fonseca,
};

type Navigate = (to: Path, options?: { replace?: boolean }) => void;

/** Back to the previous screen of the app, or to `fallback` when the app opened on this one. */
type Back = (fallback: Path) => void;

/**
 * To `target`, as a way out of the current screen: back down the history
 * when the screen below is `target`, so the history holds it once;
 * otherwise in place of the current entry.
 */
type LeaveTo = (target: Path) => void;

const RouterContext = createContext<{ path: string; navigate: Navigate; back: Back; leaveTo: LeaveTo } | null>(null);

/**
 * How many entries of the app sit below the current one in the browser's
 * history. A page opened from a link outside the app has 0: its back
 * button must not leave the app.
 */
function depth(): number {
  const state: unknown = window.history.state;
  return typeof state === "object" && state !== null && "depth" in state && typeof state.depth === "number" ? state.depth : 0;
}

/** The path of the app's entry below the current one, when it pushed this one. */
function below(): string | null {
  const state: unknown = window.history.state;
  return typeof state === "object" && state !== null && "from" in state && typeof state.from === "string" ? state.from : null;
}

export function Router({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback<Navigate>((to, options) => {
    if (options?.replace) {
      window.history.replaceState({ depth: depth(), from: below() }, "", to);
    } else {
      window.history.pushState({ depth: depth() + 1, from: window.location.pathname }, "", to);
    }
    setPath(to);
  }, []);

  const back = useCallback<Back>(
    (fallback) => {
      if (depth() > 0) window.history.back();
      else navigate(fallback, { replace: true });
    },
    [navigate],
  );

  const leaveTo = useCallback<LeaveTo>(
    (target) => {
      if (depth() > 0 && below() === target) window.history.back();
      else navigate(target, { replace: true });
    },
    [navigate],
  );

  const value = useMemo(() => ({ path, navigate, back, leaveTo }), [path, navigate, back, leaveTo]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const router = useContext(RouterContext);
  if (!router) {
    throw new Error("useRouter needs a <Router> above it");
  }
  return router;
}

/** An anchor that navigates in place; a modified click still opens a new tab. */
export function Link({
  to,
  className,
  children,
}: {
  to: Path;
  className?: string;
  children: ReactNode;
}) {
  const { navigate } = useRouter();
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} className={className} onClick={onClick}>
      {children}
    </a>
  );
}
